/**
 * System Status / Owner-Test Disclosure Surface
 * =============================================
 * Public, unauthenticated read-only page that surfaces the live integration
 * matrix (email / payments / OTP / Twilio / WhatsApp / storage / auth / DB).
 *
 * Source of truth: GET /api/system-status/public — never returns key material.
 * Used by the owner during owner-test to instantly verify which flows are
 * simulated vs real.
 *
 * NO new architecture, NO real-mode integration changes — this is a transparency
 * surface only.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CheckCircle2, AlertTriangle, ShieldCheck, RefreshCw, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_BADGE = {
  real: { label: 'Real', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  mock: { label: 'Mock', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle },
  missing: { label: 'Missing', cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: AlertTriangle },
};

const IMPACT_LABEL = {
  P0: 'Owner-test blocker',
  P1: 'Should fix before owner test',
  ok: '',
};

const SystemStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/system-status/public`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = data?.summary || { real: 0, mock: 0, missing: 0 };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="system-status-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-7 h-7 text-blue-600" />
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Owner-Test Integration Status</h1>
                <p className="text-sm text-slate-600">
                  Live disclosure of which TreatX integrations are real vs simulated.
                </p>
              </div>
            </div>
            <button
              onClick={load}
              className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="refresh-status-btn"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {err && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              Failed to load status: {err}
            </div>
          )}

          {data && (
            <>
              <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="advisory">
                {data.advisory}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-2xl font-semibold text-emerald-700">{summary.real}</p>
                  <p className="text-xs text-emerald-700">Real</p>
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-2xl font-semibold text-amber-700">{summary.mock}</p>
                  <p className="text-xs text-amber-700">Mock</p>
                </div>
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
                  <p className="text-2xl font-semibold text-rose-700">{summary.missing}</p>
                  <p className="text-xs text-rose-700">Missing</p>
                </div>
              </div>

              <ul className="mt-6 divide-y divide-slate-100" data-testid="integration-list">
                {(data.integrations || []).map((it) => {
                  const m = MODE_BADGE[it.mode] || MODE_BADGE.missing;
                  const Icon = m.icon;
                  const impact = IMPACT_LABEL[it.owner_test_impact];
                  return (
                    <li key={it.key} className="py-3 flex items-start gap-3" data-testid={`integration-${it.key}`}>
                      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium shrink-0 ${m.cls}`}>
                        <Icon className="w-3 h-3" /> {m.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900">{it.label}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{it.note}</p>
                        {impact && (
                          <p className="text-[11px] text-rose-600 mt-1 font-medium">{impact}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 text-xs text-slate-500">
                Domain: <code className="px-1 bg-slate-100 rounded">{data.domain}</code>
                <span className="mx-2">·</span>
                <a href="#/treatx-admin/email-templates" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                  Email templates <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemStatus;
